import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../components/ToastContainer';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import * as globalWorkLogApi from '../services/api/globalWorkLog';
import { useAppSelector } from '../hooks/redux';
import { usePermissions, Permission } from '../hooks/usePermissions';

interface GlobalWorkLog {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isGlobal: boolean;
  organizationName?: string;
  communityName?: string;
  managementCompany?: string;
  dutyStaff?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  selectedIssues: Array<{
    id: string;
    issue: {
      id: string;
      key: string;
      title: string;
      status: string;
      project: {
        id: string;
        name: string;
        key: string;
      };
      boardStatus?: {
        id: string;
        name: string;
        color: string;
      };
    };
  }>;
}

const GlobalWorkLogs: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { user, loading: isAuthLoading, isAuthenticated } = useAppSelector(state => state.auth);
  const { hasPermission } = usePermissions();
  const [workLogs, setWorkLogs] = useState<GlobalWorkLog[]>([]);
  const [filteredWorkLogs, setFilteredWorkLogs] = useState<GlobalWorkLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    loadWorkLogs();
  }, []);

  useEffect(() => {
    filterWorkLogs();
  }, [workLogs, searchTerm, statusFilter]);

  const loadWorkLogs = async () => {
    try {
      setIsLoading(true);
      const data = await globalWorkLogApi.getGlobalWorkLogs();
      setWorkLogs(data);
    } catch (error) {
      console.error('Failed to load global work logs:', error);
      showError('載入全域工作日誌失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const filterWorkLogs = () => {
    let filtered = workLogs;

    // 搜尋篩選
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(workLog =>
        workLog.title.toLowerCase().includes(term) ||
        workLog.communityName?.toLowerCase().includes(term) ||
        workLog.managementCompany?.toLowerCase().includes(term) ||
        workLog.dutyStaff?.toLowerCase().includes(term)
      );
    }

    // 狀態篩選
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(workLog => workLog.status === statusFilter);
    }

    setFilteredWorkLogs(filtered);
  };

  const handleDelete = async (workLogId: string) => {
    if (!window.confirm('確定要刪除這個全域工作日誌嗎？此操作無法復原。')) {
      return;
    }

    try {
      await globalWorkLogApi.deleteGlobalWorkLog(workLogId);
      setWorkLogs(prev => prev.filter(wl => wl.id !== workLogId));
      showSuccess('全域工作日誌已刪除');
    } catch (error) {
      console.error('Failed to delete global work log:', error);
      showError('刪除全域工作日誌失敗');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      DRAFT: { label: '草稿', color: 'bg-gray-100 text-gray-800' },
      PUBLISHED: { label: '已發布', color: 'bg-green-100 text-green-800' },
      ARCHIVED: { label: '已封存', color: 'bg-yellow-100 text-yellow-800' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'yyyy/MM/dd', { locale: zhTW });
  };

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), 'yyyy/MM/dd HH:mm', { locale: zhTW });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 標題和操作 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">全域工作日誌</h1>
          <p className="text-gray-600 mt-1">管理跨專案的工作日誌與會議決議執行情形</p>
        </div>
        <button
          onClick={() => navigate('/global-worklogs/create')}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          新增全域工作日誌
        </button>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜尋標題、社區名稱、管理公司或值班人員..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">所有狀態</option>
            <option value="DRAFT">草稿</option>
            <option value="PUBLISHED">已發布</option>
            <option value="ARCHIVED">已封存</option>
          </select>
        </div>
      </div>

      {/* 工作日誌列表 */}
      <div className="space-y-4">
        {filteredWorkLogs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm || statusFilter !== 'ALL' ? '沒有符合條件的工作日誌' : '還沒有全域工作日誌'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'ALL' 
                ? '請嘗試調整搜尋條件' 
                : '建立第一個全域工作日誌來開始管理跨專案的工作項目'
              }
            </p>
            {(!searchTerm && statusFilter === 'ALL') && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/global-worklogs/create')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  新增全域工作日誌
                </button>
              </div>
            )}
          </div>
        ) : (
          filteredWorkLogs.map((workLog) => (
            <div key={workLog.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{workLog.title}</h3>
                      {getStatusBadge(workLog.status)}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm text-gray-600">
                      {workLog.communityName && (
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span>社區：{workLog.communityName}</span>
                        </div>
                      )}
                      {workLog.managementCompany && (
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span>管理公司：{workLog.managementCompany}</span>
                        </div>
                      )}
                      {workLog.dutyStaff && (
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span>值班人員：{workLog.dutyStaff}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-6 text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        <span>{formatDate(workLog.startDate)} - {formatDate(workLog.endDate)}</span>
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        <span>建立於 {formatDateTime(workLog.createdAt)}</span>
                      </div>
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1" />
                        <span>建立者：{workLog.createdBy.name}</span>
                      </div>
                    </div>

                    {workLog.selectedIssues.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">
                          關聯議題 ({workLog.selectedIssues.length} 個)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {workLog.selectedIssues.slice(0, 5).map((workLogIssue) => (
                            <span
                              key={workLogIssue.id}
                              className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                            >
                              <span className="font-medium mr-1">{workLogIssue.issue.key}</span>
                              <span className="text-gray-500">({workLogIssue.issue.project.name})</span>
                            </span>
                          ))}
                          {workLog.selectedIssues.length > 5 && (
                            <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                              +{workLog.selectedIssues.length - 5} 更多
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => navigate(`/global-worklogs/${workLog.id}`)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                      title="檢視詳情"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    {/* 編輯按鈕 - 只有創建者或有管理權限的人可以編輯 */}
                    {(() => {
                      // 等待認證載入完成
                      if (isAuthLoading || !isAuthenticated) {
                        return null;
                      }
                      
                      const isOwner = user?.id === workLog.createdBy.id;
                      const hasManagePermission = hasPermission(Permission.MANAGE_GLOBAL_WORK_LOG);
                      const canEdit = isOwner || hasManagePermission;
                      
                      return canEdit && (
                        <button
                          onClick={() => navigate(`/global-worklogs/${workLog.id}/edit`)}
                          className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-md"
                          title="編輯"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => navigate(`/global-worklogs/${workLog.id}/export`)}
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md"
                      title="匯出 PDF"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(workLog.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                      title="刪除"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GlobalWorkLogs;
